import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import styles from 'components/medicalHistory/page.module.scss';
import Stack from '@mui/material/Stack';
import Button from 'components/common/button';
import OutlineButton from 'components/common/outlineButton';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import MetaTag from 'components/meta';
import Label from 'components/common/label';
import { useSelector } from 'react-redux';
import { NullablePatientDTO } from 'types/patients';
import { RootState } from 'store/store';
import { FamilyHistoryDTO } from 'artifacts/dtos';
import clsx from 'clsx';
import InputText from 'components/common/InputText';
import concat from 'lodash/concat';
import keyBy from 'lodash/keyBy';
import partition from 'lodash/partition';

const relations = ['Parent', 'Sibling', 'Child'];
const listOfOptions = [
  'Osteoporosis',
  'Osteoarthritis',
  'Blood clot',
  "Dupuytren's contracture",
  'Foot Problem',
  'Deep Vein Thrombosis (DVT)',
  'Diabetes',
  'Heart Disease',
  'Inflammatory Arthritis',
];

type FamilyHistory = FamilyHistoryDTO & { readOnly?: boolean };

function mapOptionWithSelectedData(predefineList: string[], selectedList: FamilyHistory[]) {
  const predefineMap = keyBy(predefineList);
  const [
    listedItems,
    unlistedItems,
  ] = partition(selectedList, (item) => predefineMap[item.diseaseName!]);

  const selectedOption = concat(listedItems, unlistedItems.map(
    (item) => ({ ...item, readOnly: true }),
  ));
  const updatedOptions = concat(predefineList, unlistedItems.map((item) => item.diseaseName));

  return { selectedOption, updatedOptions };
}

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function FamilyHistoryScreen({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;
  const [optionList, setOptionList] = useState<string[]>(listOfOptions);
  const [selectedOption, setSelectedOption] = useState<FamilyHistory[]>([]);
  const [state, dispatch] = usePersistMHReducer();

  const clickNextOrSubmit = async (nextPage: number) => {
    await dispatch({
      type: actionPage,
      name: 'Family History',
      value: selectedOption,
    });

    nextOrSubmitButton(nextPage);
  };

  useEffect(() => {
    if (state) {
      const familyHistory = state.find(
        (p: StateTypeLocalStorage) => p.name === actionPage,
      );
      if (familyHistory?.value.length) {
        const {
          selectedOption,
          updatedOptions,
        } = mapOptionWithSelectedData(
          listOfOptions,
          familyHistory?.value,
        );
        setOptionList(updatedOptions as string[]);
        setSelectedOption(selectedOption);
      } else if (patient?.familyHistories.length) {
        const { selectedOption, updatedOptions } = mapOptionWithSelectedData(
          listOfOptions,
          patient?.familyHistories,
        );
        setOptionList(updatedOptions as string[]);
        setSelectedOption(selectedOption);
      }
    }
  }, []);

  const onClickOption = (diseaseName: string) => {
    setSelectedOption((prev) => (prev?.some((item) => item.diseaseName === diseaseName)
      ? prev.filter((data) => data.diseaseName !== diseaseName)
      : [...prev, { diseaseName, diseaseDescription: '', familyRelation: '' }]));
  };

  const onClickRelation = (diseaseName: string, relation: string) => {
    setSelectedOption((prev) => {
      const existingItem = prev?.find((item) => item.diseaseName === diseaseName);

      if (existingItem) {
        const updatedData = {
          diseaseName,
          familyRelation: relation,
          diseaseDescription: existingItem.diseaseDescription,
        };

        const updatedList = prev.filter((data) => data.diseaseName !== diseaseName);
        return [...updatedList, updatedData];
      }

      return [
        ...prev,
        {
          diseaseName,
          diseaseDescription: '',
          familyRelation: relation,
        },
      ];
    });
  };

  const onChangeDiseaseDescription = (diseaseName: string, diseaseDescription: string) => {
    setSelectedOption((prev) => {
      const existingItem = prev?.find((item) => item.diseaseName === diseaseName);

      if (existingItem) {
        const updatedData = {
          ...existingItem,
          diseaseDescription,
        };

        const updatedList = prev.filter((data) => data.diseaseName !== diseaseName);
        return [...updatedList, updatedData];
      }

      return [
        ...prev,
        {
          diseaseName,
          diseaseDescription,
          familyRelation: '',
        },
      ];
    });
  };

  return (
    <>
      <MetaTag title="Family History" />
      <Box pb={3} mb={4}>
        <Box>
          <h2 className={`marginTopZero ${styles.titleText}`}>
            Family History
          </h2>
          <Stack>
            <Label>
              Has anybody in your immediate family ever had any of the following?
            </Label>
            {
              optionList.map((item) => {
                const selected = selectedOption.find((data) => data.diseaseName === item);
                return (
                  <div key={item}>
                    <p
                      className={
                        clsx(
                          { 'cursor-pointer': !selected?.readOnly },
                          { 'cursor-not-allowed !text-gray-500 !bg-gray-200 font-medium': selected?.readOnly },
                          {
                            'selected-option': selected?.diseaseName === item
                            && !selected?.readOnly,
                          },
                          'option',
                        )
                      }
                      onKeyUp={() => {}}
                      onClick={() => {
                        if (!selected?.readOnly) {
                          onClickOption(item);
                        }
                      }}
                    >
                      {item}
                    </p>
                    {selected?.diseaseName === item && (
                      <div className="flex gap-2 mb-2">
                        {relations.map((relation) => (
                          <OutlineButton
                            onClick={() => {
                              if (!selected?.readOnly) {
                                onClickRelation(item, relation);
                              }
                            }}
                            isActive={selected?.familyRelation === relation}
                            key={relation}
                            className={clsx(
                              '!normal-case !font-normal',
                              { '!cursor-not-allowed !text-gray-500 !bg-gray-200': selected?.readOnly },
                            )}
                          >
                            {relation}
                          </OutlineButton>
                        ))}
                      </div>
                    )}
                    {(selected?.diseaseName === item && item === 'Foot Problem') && (
                      <div className="flex flex-col">
                        <Label>
                          What foot problem?
                        </Label>
                        <InputText
                          onChange={
                                (event) => { onChangeDiseaseDescription(item, event.target.value); }
                              }
                          value={selected?.diseaseDescription ?? ''}
                          name="disease"
                        />
                      </div>
                    )}
                  </div>
                );
              })
            }
          </Stack>
        </Box>
        <div className="next-button-fixed">
          <Button onClick={() => clickNextOrSubmit(nextPage)}>
            Next
          </Button>
        </div>
      </Box>
    </>
  );
}

export default FamilyHistoryScreen;
